import { TranslationKey } from './../../../../types/translation/TranslationKey';

import { useMutation } from '@apollo/client';
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnboardingData, OnboardingRoutes } from '..';
import {
  OnboardCompanyResponse,
  OnboardCompanyVariables,
  OnboardingMutations,
} from '../../../../graphql/onboarding.graphql';
import { t } from '../../../../types/translation/Translator';
import { Button } from '../../../components/Button';
import MultiEmailInput from '../../../components/MultiTextInput/templates/MultiEmailInput';
import { OnboardingStep, OnboardingSubtitle, OnboardingTitle } from '../components';
import OnboardingPaneHeader from '../components/OnboardingPaneHeader';
import { flattenOnboardingEntities } from '../model/OnboardingData';
import { ToastType } from '../../../components/Toast';
import { UserContext } from '../../../../context/UserContext';
import { OnboardingLS } from '../../../../util/local_storage/onboarding.ls';
import { storeCurrentCompanyId } from '../../../../util/token.util';
import { useToast } from '../../../../providers/ToastProvider';

interface OnboardingUserPaneProps {
  data: OnboardingData;
  setData: (data: OnboardingData) => void;
}

export default function OnboardingUserPane({ data, setData }: OnboardingUserPaneProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { currentUser } = useContext(UserContext);

  const [onboard, { loading }] = useMutation<OnboardCompanyResponse, OnboardCompanyVariables>(
    OnboardingMutations.onboardCompany,
    {
      variables: {
        data: {
          company: data.company,
          entities: flattenOnboardingEntities(data.summary),
          users: [...data.users],
        },
      },
      onError: error => {
        showToast({ type: ToastType.error, title: error.message });
      },
      onCompleted: data => {
        storeCurrentCompanyId(data.onboardCompany.id);
        OnboardingLS.removeOnboardingData();
        OnboardingLS.setOnboardedState('DEFAULT');
        navigate('/dashboard');
        window.location.reload();
      },
    },
  );

  return (
    <div className='grid gap-4'>
      <OnboardingPaneHeader back={() => navigate(OnboardingRoutes.stockImport)} />

      <div className='grid gap-1'>
        <OnboardingStep />
        <OnboardingTitle text={'Add your coworkers'} />
        <OnboardingSubtitle
          text={
            'Add users to the platform to allow your coworkers to collaborate on inventory management, track stock efficiently and streamline operations'
          }
        />
      </div>
      <p className='text-sm text-gray-600'>
        Users will initially be invited as <b>Administrators</b>. You can later inivite additional users and modify
        their roles as needed.
      </p>
      <MultiEmailInput
        invalid={currentUser ? new Set([currentUser?.email]) : new Set()}
        label={'Invite users by email'}
        emails={data.users}
        setEmails={value => {
          data.users = value;
          setData(data);
        }}
      />

      <Button
        text={t(TranslationKey.finish).singular.label}
        onClick={() => onboard()}
        className='w-fit'
        loading={loading}
      />
    </div>
  );
}
